import { useEffect, useState } from 'react';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { bulkUpdateCustomers } from '@/store/slices/contacts/contactsSlice';
import { useToast } from '@/components/ui/use-toast';
import { HttpClient } from '@/api/services/HttpClient';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Select from 'react-select';

interface TagSelectorDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedContacts: string[];
}

interface Tag {
    id: string;
    name: string;
    color: string;
}

interface TagOption {
    value: string;
    label: string;
    color: string;
}

interface TagResponse {
    status: string;
    message: string;
    data: Tag[];
}

export const TagSelectorDialog = ({ open, onOpenChange, selectedContacts }: TagSelectorDialogProps) => {
    const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const dispatch = useAppDispatch();
    const { toast } = useToast();

    useEffect(() => {
        if (open) {
            const fetchTags = async () => {
                setIsLoadingTags(true);
                try {
                    const response = await HttpClient.apiClient.get<TagResponse>('/tag', {
                        params: {
                            page: 1,
                            limit: 1000,
                            sort_by: 'createdAt',
                            sort_order: 'desc',
                        }
                    });
                    if (response.data?.status === 'success') {
                        setAvailableTags(response.data.data || []);
                    }
                } catch (error) {
                    console.error('Error fetching tags:', error);
                } finally {
                    setIsLoadingTags(false);
                }
            };
            fetchTags();
        }
    }, [open]);

    const handleSaveTags = async () => {
        const tagIds = selectedTags.map(tag => tag.value);
        await dispatch(bulkUpdateCustomers({
            customerIds: selectedContacts,
            tagIds,
        }));
        toast({
            title: "Tags updated",
            description: "Tags have been updated for the selected contacts.",
        });
        onOpenChange(false);
    };

    const tagOptions: TagOption[] = availableTags.map(tag => ({
        value: tag.id,
        label: tag.name,
        color: tag.color
    }));

    const formatOptionLabel = ({ label, color }: TagOption) => (
        <div className="flex items-center">
            <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: color }} />
            <span>{label}</span>
        </div>
    );

    const customStyles = {
        multiValue: (styles, { data }) => ({
            ...styles,
            backgroundColor: data.color,
            color: 'white',
        }),
        multiValueLabel: (styles) => ({
            ...styles,
            color: 'white',
        }),
        multiValueRemove: (styles) => ({
            ...styles,
            color: 'white',
            ':hover': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'white',
            },
        }),
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add Tags to Contacts</DialogTitle>
                </DialogHeader>
                <Select
                    isMulti
                    value={selectedTags}
                    onChange={(selected) => setSelectedTags(selected as TagOption[])}
                    options={tagOptions}
                    className="w-full text-xs"
                    placeholder="Select tags"
                    isLoading={isLoadingTags}
                    noOptionsMessage={() => availableTags.length > 0 ? "No matching tags" : "No tags available"}
                    formatOptionLabel={formatOptionLabel}
                    styles={customStyles}
                />
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={handleSaveTags}>Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
